// Regras de arquivo do card-file: limites, tipos aceitos, compressão de foto,
// duração de vídeo e nome do arquivo. Sem Vue aqui, para poder testar isolado.

export const LIMITE_BYTES = 10 * 1024 * 1024; // 10 MB por arquivo
export const LIMITE_VIDEO_SEGUNDOS = 5 * 60; // 5 minutos
export const LIMITE_NOME = 60;

// Lado maior da foto depois de comprimida. 1920 px mantém placa/lacre legíveis e
// deixa a foto perto de 300–600 KB em JPEG (a câmera crua passa de 10 MB em PNG).
export const FOTO_LADO_MAX = 1920;
export const FOTO_QUALIDADE = 0.85;

// Gravação: 220 kbps de vídeo + 32 kbps de áudio ≈ 9,4 MB em 5 min, então os dois
// limites batem juntos. O tamanho também é medido durante a gravação (ver
// CameraCaptura), porque nem todo encoder respeita o bitrate pedido.
export const VIDEO_BITS = 220000;
export const AUDIO_BITS = 32000;

// Mesmas categorias que a API aceita (FormularioRespostaController.$respostaComArquivo).
export const TIPOS = {
  pdf: { mimes: ['application/pdf'], exts: ['pdf'] },
  txt: { mimes: ['text/plain'], exts: ['txt'] },
  doc: {
    mimes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
      'text/xml',
    ],
    exts: ['doc', 'docx', 'odt', 'xml'],
  },
  xls: {
    mimes: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet',
    ],
    exts: ['xls', 'xlsx', 'ods'],
  },
  imagem: {
    mimes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'],
    exts: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'],
  },
  video: {
    mimes: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
    exts: ['mp4', 'webm', 'ogv', 'mov'],
  },
};

export const ACCEPT_DOCUMENTO = ['pdf', 'txt', 'doc', 'xls']
  .reduce((acc, t) => acc.concat(TIPOS[t].exts.map((e) => `.${e}`)), [])
  .join(',');

// 'video/webm;codecs=vp8,opus' -> 'video/webm'
export function mimeBase(mime) {
  return String(mime || '').split(';')[0].trim().toLowerCase();
}

export function extensao(nome) {
  const m = /\.([a-z0-9]+)$/i.exec(String(nome || ''));
  return m ? m[1].toLowerCase() : '';
}

export function semExtensao(nome) {
  return String(nome || '').replace(/\.[a-z0-9]+$/i, '');
}

// Categoria pelo mime; sem mime (comum em documento no Android), pela extensão.
export function categoria(mime, nome) {
  const base = mimeBase(mime);
  const ext = extensao(nome);
  return Object.keys(TIPOS).find((t) => (base && TIPOS[t].mimes.includes(base)))
    || Object.keys(TIPOS).find((t) => (ext && TIPOS[t].exts.includes(ext)))
    || null;
}

// 'imagem' | 'video' | 'documento' — o que a miniatura/preview mostra.
export function tipoMidia(mime, nome) {
  const cat = categoria(mime, nome);
  if (cat === 'imagem') return 'imagem';
  if (cat === 'video') return 'video';
  return 'documento';
}

// Extensão que o arquivo final deve ter, a partir do mime.
export function extensaoPorMime(mime, nomeOriginal) {
  const base = mimeBase(mime);
  const mapa = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/webp': 'webp',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/ogg': 'ogv',
    'video/quicktime': 'mov',
  };
  return mapa[base] || extensao(nomeOriginal) || '';
}

// O nome vira caminho no servidor (uploads/respostas/<form>/<questão>/<nome>):
// sem acento, sem separador de diretório e sem caracteres que quebram a URL.
export function sanitizarNome(nome) {
  return String(nome || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9 _-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, LIMITE_NOME);
}

// 20261014_153012
export function carimbo(data = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${data.getFullYear()}${p(data.getMonth() + 1)}${p(data.getDate())}_${p(data.getHours())}${p(data.getMinutes())}${p(data.getSeconds())}`;
}

// Nome usado quando o usuário deixa o campo vazio: foto_/video_/arquivo_ + data e
// hora, com _2, _3... se colidir com algum de `usados` (nomes completos, minúsculos).
export function nomeAutomatico(tipo, ext, usados, data = new Date()) {
  const prefixo = { imagem: 'foto', video: 'video' }[tipo] || 'arquivo';
  const base = `${prefixo}_${carimbo(data)}`;
  let nome = base;
  for (let n = 2; usados.has(`${nome}.${ext}`.toLowerCase()); n += 1) nome = `${base}_${n}`;
  return nome;
}

export function formatarTamanho(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
}

export function formatarTempo(segundos) {
  const s = Math.max(0, Math.floor(Number(segundos) || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// Primeiro formato que este navegador grava. mp4 primeiro (iOS e Chrome 126+);
// Chrome Android antigo só grava webm, e mp4 fixo fazia o MediaRecorder lançar erro.
export function escolherMimeVideo() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  const candidatos = [
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
  ];
  return candidatos.find((m) => MediaRecorder.isTypeSupported(m)) || '';
}

export function podeGravarVideo() {
  return typeof MediaRecorder !== 'undefined';
}

export function temCameraWeb() {
  return !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

function canvasParaBlob(canvas, tipo, qualidade) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => resolve(blob), tipo, qualidade);
      return;
    }
    // Navegadores muito antigos: passa por dataURL.
    const dataUrl = canvas.toDataURL(tipo, qualidade);
    const [cab, b64] = dataUrl.split(',');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    resolve(new Blob([bytes], { type: cab.match(/:(.*?);/)[1] }));
  });
}

// Desenha a fonte (vídeo da câmera ou imagem) reduzida para FOTO_LADO_MAX e exporta JPEG.
// O fundo branco evita que PNG transparente vire preto no JPEG.
export async function desenharJpeg(fonte, largura, altura, { espelhar = false } = {}) {
  const escala = Math.min(1, FOTO_LADO_MAX / Math.max(largura, altura));
  const w = Math.round(largura * escala);
  const h = Math.round(altura * escala);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  if (espelhar) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(fonte, 0, 0, w, h);
  return canvasParaBlob(canvas, 'image/jpeg', FOTO_QUALIDADE);
}

function carregarImagem(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('imagem ilegível')); };
    img.src = url;
  });
}

// Foto da galeria: recomprime só quando passa do tamanho de tela ou de 2 MB.
// GIF fica como está (perderia a animação).
export async function comprimirImagem(file) {
  const base = mimeBase(file.type);
  if (base === 'image/gif') return file;
  let img;
  try {
    img = await carregarImagem(file);
  } catch (e) {
    return file;
  }
  const grande = Math.max(img.naturalWidth, img.naturalHeight) > FOTO_LADO_MAX;
  if (!grande && file.size <= 2 * 1024 * 1024) return file;
  const blob = await desenharJpeg(img, img.naturalWidth, img.naturalHeight);
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], `${semExtensao(file.name) || 'foto'}.jpg`, { type: 'image/jpeg' });
}

// Duração em segundos, ou null se o navegador não souber dizer.
// WebM gravado pelo Chrome vem com duração Infinity: pular para o fim força o cálculo.
export function duracaoVideo(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    let terminou = false;
    const fim = (valor) => {
      if (terminou) return;
      terminou = true;
      URL.revokeObjectURL(url);
      v.removeAttribute('src');
      resolve(Number.isFinite(valor) ? valor : null);
    };
    const timeout = setTimeout(() => fim(null), 8000);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration)) {
        clearTimeout(timeout);
        fim(v.duration);
        return;
      }
      v.ondurationchange = () => {
        if (Number.isFinite(v.duration)) {
          clearTimeout(timeout);
          fim(v.duration);
        }
      };
      v.currentTime = 1e101;
    };
    v.onerror = () => { clearTimeout(timeout); fim(null); };
    v.src = url;
  });
}

// Valida e prepara um arquivo vindo da galeria/documento/câmera nativa.
// Resolve { ok: true, file, tipo, duracao } ou { ok: false, motivo }.
export async function prepararArquivo(original) {
  const nome = original.name || 'arquivo';
  const cat = categoria(original.type, nome);
  if (!cat) return { ok: false, motivo: `${nome}: tipo de arquivo não permitido` };

  let file = original;
  if (cat === 'imagem') file = await comprimirImagem(original);

  if (file.size > LIMITE_BYTES) {
    return { ok: false, motivo: `${nome}: o arquivo não pode ser maior que 10 MB (${formatarTamanho(file.size)})` };
  }

  let duracao = null;
  if (cat === 'video') {
    duracao = await duracaoVideo(file);
    if (duracao != null && duracao > LIMITE_VIDEO_SEGUNDOS + 0.5) {
      return { ok: false, motivo: `${nome}: o vídeo não pode passar de 5 minutos (${formatarTempo(duracao)})` };
    }
  }

  return {
    ok: true, file, tipo: tipoMidia(file.type, file.name), duracao,
  };
}

// Baixa uma cópia para o aparelho (vai para Downloads/galeria, como nas câmeras dos apps).
export function salvarCopia(file) {
  try {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) {
    // Cópia é conveniência: falhar aqui não pode impedir a resposta.
  }
}
